import { useEffect, useState } from "react";
import { makeRequest } from "../utils/request";

export interface SbcRegionOption {
  // The reserved telephonyId to assign a number to this SBC: bare "voicerun-sbc"
  // for the default region, "voicerun-sbc:<region>" otherwise.
  telephonyId: string;
  region: string;
  label: string;
  isDefault: boolean;
}

interface SbcRegionsResponse {
  regions: SbcRegionOption[];
}

/**
 * Fetches the platform (VoiceRun-owned) SBC regions an admin can assign a number
 * to (GET /v1/telephony/sbc-regions). The endpoint is admin-only, so callers must
 * gate on admin and pass `enabled` accordingly to avoid a guaranteed 403.
 */
export const useSbcRegions = (enabled: boolean) => {
  const [regions, setRegions] = useState<SbcRegionOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRegions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await makeRequest<SbcRegionsResponse>("/v1/telephony/sbc-regions");
        if (!cancelled) setRegions(data.regions ?? []);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to fetch SBC regions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { regions, loading, error };
};
